import React from 'react';
import { AlertTriangleIcon, CheckCircle2Icon, CheckIcon, Loader2Icon, RefreshCwIcon, ShieldCheckIcon } from 'lucide-react';
import type { ValidationState } from '../types/disclosures';
interface ReadinessCardProps {
  validation: ValidationState;
  canGenerate: boolean;
  onGenerate: () => void;
  onRetryValidation: () => void;
  onIssueAction: (issueId: string) => void;
}
export function ReadinessCard({
  validation,
  canGenerate,
  onGenerate,
  onRetryValidation,
  onIssueAction
}: ReadinessCardProps) {
  if (validation.phase === 'running') {
    return <section className="rounded-xl border border-slate-200 bg-white p-5" data-id="element-206" data-mp="btk9tgxmy958">
        <h3 className="text-[14px] font-semibold text-slate-900" data-id="element-207" data-mp="bvoomtx2oska">Disclosure Readiness</h3>
        <div className="mt-3 flex items-center gap-2 text-[13px] text-slate-500" data-id="element-208" data-mp="71kg4685sch3">
          <Loader2Icon className="h-4 w-4 animate-spin text-slate-400" aria-hidden="true" data-id="element-209" data-mp="3fh44orxvx6pt" />
          Checking the loan for disclosure readiness…
        </div>
        <div className="mt-4 space-y-2" aria-hidden="true" data-id="element-210" data-mp="1laskk6gmm4b2">
          {[0, 1, 2, 3].map(row => <div key={row} className="h-3 w-full animate-pulse rounded bg-slate-100" data-id="element-211" data-mp="1q8u1oluhrtjw" />)}
        </div>
      </section>;
  }
  if (validation.phase === 'issues') {
    return <section className="overflow-hidden rounded-xl border border-amber-200 bg-white" data-id="element-212" data-mp="1zyvq5eqrbdzc">
        <div className="flex items-start gap-3 border-b border-amber-100 bg-amber-50/70 px-5 py-4" data-id="element-213" data-mp="12benhfnlodgc">
          <AlertTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" data-id="element-214" data-mp="17ahuv6r1yvrh" />
          <div data-id="element-215" data-mp="1c9kprydbjllq">
            <h3 className="text-[14px] font-semibold text-amber-900" data-id="element-216" data-mp="1c91f92pzzzhq">
              {validation.issues.length} {validation.issues.length === 1 ? 'item' : 'items'} need
              attention
            </h3>
            <p className="mt-0.5 text-[13px] text-amber-800/80" data-id="element-217" data-mp="2npj0s7s24hpl">
              Resolve these items before generating disclosures.
            </p>
          </div>
        </div>
        <ul className="divide-y divide-slate-100" data-id="element-218" data-mp="slb9h3ymychl">
          {validation.issues.map(issue => <li key={issue.id} className="flex items-start gap-3 px-5 py-3.5" data-id="element-219" data-mp="nmqjdxd3l96v">
              <AlertTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" data-id="element-220" data-mp="noukk7jm3u3g" />
              <div className="min-w-0 flex-1" data-id="element-221" data-mp="xlpl0vzg44kd">
                <p className="text-[13px] font-semibold text-slate-900" data-id="element-222" data-mp="xitmb5frxbuv">{issue.title}</p>
                <p className="mt-0.5 text-[13px] leading-5 text-slate-500" data-id="element-223" data-mp="9szamr76xtc7">{issue.description}</p>
              </div>
              {issue.actionLabel && <button type="button" onClick={() => onIssueAction(issue.id)} className="shrink-0 rounded-md border border-slate-200 px-2.5 py-1.5 text-[12px] font-medium text-slate-700 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" data-id="element-224" data-mp="iof9jmmsyyon">
                  {issue.actionLabel}
                </button>}
            </li>)}
        </ul>
        <div className="flex items-center justify-between gap-3 border-t border-slate-100 bg-slate-50/60 px-5 py-3.5" data-id="element-225" data-mp="3cxexu0uejww5">
          <p className="text-[12px] text-slate-500" data-id="element-226" data-mp="2y9k4qjchfqij">
            Validation runs automatically once the loan is updated.
          </p>
          <button type="button" onClick={onRetryValidation} className="inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-2 text-[13px] font-semibold text-slate-800 ring-1 ring-slate-200 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" data-id="element-227" data-mp="33851g8sizs28">
            <RefreshCwIcon className="h-3.5 w-3.5" data-id="element-228" data-mp="3ly3dezzg25b5" />
            Retry Validation
          </button>
        </div>
      </section>;
  }
  return <section className="rounded-xl border border-slate-200 bg-white" data-id="element-229" data-mp="25ijys2ulcco8">
      <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-3.5" data-id="element-230" data-mp="2ak991g9zomb9">
        <ShieldCheckIcon className="h-4 w-4 text-slate-400" aria-hidden="true" data-id="element-231" data-mp="2qquhhqxdiijn" />
        <h3 className="text-[14px] font-semibold text-slate-900" data-id="element-232" data-mp="2lzw2cuo4fqhz">Disclosure Readiness</h3>
      </div>

      <ul className="grid gap-2.5 px-5 py-4 sm:grid-cols-2" data-id="element-233" data-mp="2h09oqk4ds88n">
        {validation.checks.map(check => <li key={check.id} className="flex items-center gap-2 text-[13px] text-slate-700" data-id="element-234" data-mp="2c1yei8a6bvpw">
            <CheckIcon className="h-4 w-4 shrink-0 text-emerald-600" strokeWidth={2.5} data-id="element-235" data-mp="39ox2zvqppxcp" />
            {check.label}
          </li>)}
      </ul>

      <div className="flex flex-col gap-4 border-t border-slate-100 bg-emerald-50/40 px-5 py-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-236" data-mp="34qnajswdhaus">
        <div className="flex items-start gap-3" data-id="element-237" data-mp="2us6c6av3ttyp">
          <CheckCircle2Icon className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" aria-hidden="true" data-id="element-238" data-mp="2urefsxtons7d" />
          <div data-id="element-239" data-mp="1jcr4od59rjpc">
            <p className="text-[14px] font-semibold text-slate-900" data-id="element-240" data-mp="1jc9cpspn3rn8">Ready to Generate</p>
            <p className="mt-0.5 text-[13px] text-slate-600" data-id="element-241" data-mp="3v0e77b6xzcxt">
              The loan has been validated and the disclosure package can be created.
            </p>
          </div>
        </div>
        <button type="button" onClick={onGenerate} disabled={!canGenerate} className="shrink-0 rounded-md bg-slate-900 px-4 py-2.5 text-[13px] font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2" data-id="element-242" data-mp="8swekt2d3czs">
          Generate Disclosures
        </button>
      </div>
    </section>;
}